import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { PublicKey } from 'ox';
import { parseBase64url } from '@portafino/shared';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Format a currency amount with symbol
 */
export function formatCurrency(amount: number, currency: string = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(amount);
}

/**
 * Truncate a string in the middle with ellipsis
 */
export function truncateMiddle(str: string, frontChars: number = 6, endChars: number = 4): string {
  if (!str) return '';
  if (str.length <= frontChars + endChars) return str;
  
  return `${str.substring(0, frontChars)}...${str.substring(str.length - endChars)}`;
}

/**
 * Parse URL parameters to get specific values
 */
export function getUrlParam(paramName: string): string | null {
  if (typeof window === 'undefined') return null;
  
  const params = new URLSearchParams(window.location.search);
  return params.get(paramName);
}

/**
 * Generates a random job ID for demo purposes
 */
export function generateDemoJobId(): string {
  return `JOB-${Math.floor(Math.random() * 10000).toString().padStart(4, '0')}`;
}

export async function toPublicKey(publicKeyB64: string) {
  const cryptoKey = await crypto.subtle.importKey(
    'spki',
    parseBase64url(publicKeyB64),
    {
      name: 'ECDSA',
      namedCurve: 'P-256',
    },
    true,
    ['verify'],
  )

  const publicKey = new Uint8Array(
    await crypto.subtle.exportKey('raw', cryptoKey),
  )
  
  return PublicKey.from(publicKey)
}
